/**
 * Tenant 식별 유틸리티 (Company B2B 전용)
 *
 * 개발 환경: 서브도메인 기반으로 tenant 식별
 *   - lgcns.localhost:3300 → lgcns
 *   - samsung.localhost:3300 → samsung
 *   - localhost:3300 (서브도메인 없음) → 랜딩 페이지
 *
 * 프로덕션 환경: 도메인에서 tenant 추출
 *   - lgcns.aiqlab.tv → lgcns
 *   - aiqlab.tv (서브도메인 없음) → 랜딩 페이지
 *
 * 스테이징 환경: staging 서브도메인 포함
 *   - lgcns.staging.aiqlab.tv → lgcns
 *   - staging.aiqlab.tv (서브도메인 없음) → 랜딩 페이지
 *
 * 도메인 구조:
 * - 회사 B2B: *.aiqlab.tv (프로덕션), *.staging.aiqlab.tv (스테이징)
 *
 * @see docs/troubleshooting/tenant-identification.md
 */

/**
 * 도메인별 조직 타입 설정
 * Company 전용 앱이므로 company 도메인만 포함
 */
export const DOMAIN_CONFIG: Record<string, { type: string; baseDomain: string }> = {
    "aiqlab.tv": { type: "company", baseDomain: "aiqlab.tv" },
    "staging.aiqlab.tv": { type: "company", baseDomain: "staging.aiqlab.tv" },
};

/**
 * 알려진 base 도메인 목록 (길이 긴 것부터 = longest match first)
 * staging 등 multi-level 도메인을 올바르게 감지하기 위해 사용
 */
const KNOWN_BASE_DOMAINS = ["staging.aiqlab.tv", "aiqlab.tv"];

/**
 * 테넌트별 기본 로고 URL 매핑
 * DB의 logo_url이 null일 때 사용되는 fallback 로고
 * 새 테넌트 추가 시 public/ 폴더에 로고 파일 추가 필요
 */
export const DEFAULT_TENANT_LOGOS: Record<string, string> = {
    "lgcns": "/lgcns-logo.png",
    "samsung": "/samsung-logo.svg",
    "demo": "/quebon-logo.svg",
};

/**
 * 테넌트 로고 URL 가져오기
 * 1순위: API에서 받은 logoUrl
 * 2순위: 테넌트별 기본 로고
 * 3순위: null (첫 글자 배지 표시)
 */
export const getTenantLogoUrl = (tenantId: string | null, apiLogoUrl: string | null | undefined): string | null => {
    // API에서 받은 logoUrl이 있으면 사용
    if (apiLogoUrl) {
        return apiLogoUrl;
    }

    // 테넌트별 기본 로고
    if (tenantId && DEFAULT_TENANT_LOGOS[tenantId]) {
        return DEFAULT_TENANT_LOGOS[tenantId];
    }

    return null;
};

/**
 * 예약된 서브도메인 (tenant로 사용 불가)
 */
const RESERVED_SUBDOMAINS = ["www", "b2b", "admin", "api", "app", "lc"];

/**
 * 로컬 개발 환경인지 확인
 * ⚠️ import.meta.env.PROD 대신 이 함수 사용 권장
 */
export const isLocalDevelopment = (): boolean => {
    const hostname = window.location.hostname;
    return (
        hostname === "localhost" ||
        hostname.endsWith(".localhost") ||
        hostname.startsWith("127.") ||
        hostname.startsWith("192.168.") ||
        hostname.endsWith(".local") ||
        hostname.endsWith(".nip.io")
    );
};

/**
 * 호스트네임에서 베이스 도메인 추출
 * 예: lgcns.aiqlab.tv → aiqlab.tv
 *     lgcns.staging.aiqlab.tv → staging.aiqlab.tv
 *     staging.aiqlab.tv → staging.aiqlab.tv
 *     lgcns.localhost → localhost
 *     lgcns.192.168.1.5.nip.io → 192.168.1.5.nip.io
 */
export const getBaseDomain = (hostname: string): string | null => {
    // localhost 서브도메인 처리
    if (hostname.endsWith(".localhost")) {
        return "localhost";
    }
    if (hostname === "localhost") {
        return "localhost";
    }

    // nip.io 처리 (LAN 접속: lgcns.192.168.1.5.nip.io → 192.168.1.5.nip.io)
    if (hostname.endsWith(".nip.io")) {
        const match = hostname.match(/(\d+\.\d+\.\d+\.\d+\.nip\.io)$/);
        return match ? match[1] : null;
    }

    // 알려진 multi-level base 도메인 우선 매칭 (longest match first)
    for (const domain of KNOWN_BASE_DOMAINS) {
        if (hostname === domain || hostname.endsWith("." + domain)) {
            return domain;
        }
    }

    // Fallback: 마지막 2개 부분
    const parts = hostname.split(".");
    if (parts.length >= 2) {
        return parts.slice(-2).join(".");
    }
    return null;
};

/**
 * 호스트네임에서 서브도메인 추출
 * base 도메인을 감지하여 그 앞부분을 서브도메인으로 반환
 * 예: lgcns.aiqlab.tv → lgcns
 *     lgcns.staging.aiqlab.tv → lgcns
 *     staging.aiqlab.tv → null (base 도메인 자체)
 *     lgcns.localhost → lgcns
 *     localhost → null (서브도메인 없음)
 */
export const getSubdomain = (hostname: string): string | null => {
    // localhost 서브도메인 처리 (예: lgcns.localhost)
    if (hostname.endsWith(".localhost")) {
        const subdomain = hostname.replace(".localhost", "");
        return subdomain || null;
    }

    // localhost만 있으면 서브도메인 없음
    if (hostname === "localhost") {
        return null;
    }

    // base 도메인 감지 후, 그 앞부분을 서브도메인으로 추출
    const baseDomain = getBaseDomain(hostname);
    if (baseDomain && hostname !== baseDomain) {
        const subdomain = hostname.slice(0, -(baseDomain.length + 1));
        return subdomain || null;
    }

    return null;
};

/**
 * 현재 도메인의 조직 타입 가져오기
 * Company 전용 앱이므로 항상 "company" 반환
 */
export const getOrganizationType = (): string => {
    return "company";
};

/**
 * 랜딩 페이지인지 확인
 * 서브도메인이 없거나 예약된 서브도메인이면 랜딩 페이지
 */
export const isLandingPage = (): boolean => {
    const hostname = window.location.hostname;
    const subdomain = getSubdomain(hostname);
    return subdomain === null || RESERVED_SUBDOMAINS.includes(subdomain);
};

/**
 * 현재 tenant ID(subdomain) 가져오기
 * 서브도메인 기반 멀티테넌시
 * 
 * 우선순위:
 * 1. 서브도메인에서 추출 (lgcns.localhost:3300 → lgcns)
 * 2. 쿼리파라미터 (?tenant=xxx) - 개발/테스트용
 * 3. localStorage - 세션 유지용
 * 
 * 서브도메인이 없으면 null 반환 (랜딩 페이지)
 */
export const getTenantId = (): string | null => {
    const hostname = window.location.hostname;
    
    // 1. 서브도메인에서 추출
    const subdomain = getSubdomain(hostname);
    
    if (subdomain && !RESERVED_SUBDOMAINS.includes(subdomain)) {
        localStorage.setItem("tenant", subdomain);
        console.log(`[Tenant] Subdomain tenant: ${subdomain}`);
        return subdomain;
    }
    
    // 2. 쿼리파라미터 확인 (개발/테스트용)
    const params = new URLSearchParams(window.location.search);
    const paramTenant = params.get("tenant");
    if (paramTenant) {
        localStorage.setItem("tenant", paramTenant);
        console.log(`[Tenant] Tenant from query param: ${paramTenant}`);
        return paramTenant;
    }
    
    // 3. 서브도메인 없음 = 랜딩 페이지
    // localStorage 값은 사용하지 않음 (랜딩에서 특정 테넌트로 자동 이동 방지)
    console.log(`[Tenant] No subdomain - landing page mode`);
    return null;
};

/**
 * 디버깅용: 현재 tenant 설정 상태 출력
 * 브라우저 콘솔에서 debugTenant() 호출하여 사용
 */
export const debugTenant = (): void => {
    const hostname = window.location.hostname;
    console.group("🔍 Tenant Debug Info (Company B2B)");
    console.log("hostname:", hostname);
    console.log("port:", window.location.port);
    console.log("isLocalDevelopment:", isLocalDevelopment());
    console.log("baseDomain:", getBaseDomain(hostname));
    console.log("subdomain:", getSubdomain(hostname));
    console.log("isLandingPage:", isLandingPage());
    console.log("organizationType:", getOrganizationType());
    console.log("getTenantId():", getTenantId());
    console.log("localStorage.tenant:", localStorage.getItem("tenant"));
    console.log("DOMAIN_CONFIG:", DOMAIN_CONFIG);
    console.groupEnd();
};

// 개발 환경에서 전역으로 디버그 함수 노출
if (typeof window !== "undefined" && isLocalDevelopment()) {
    (window as unknown as Record<string, unknown>).debugTenant = debugTenant;
}

/**
 * Tenant ID 설정 (개발용)
 */
export const setTenantId = (tenantId: string): void => {
    localStorage.setItem("tenant", tenantId);
};

/**
 * Tenant ID 초기화
 */
export const clearTenantId = (): void => {
    localStorage.removeItem("tenant");
};

/**
 * CSS 변수로 테넌트 테마 적용
 */
export const applyTenantTheme = (primaryColor: string | null): void => {
    if (primaryColor) {
        document.documentElement.style.setProperty(
            "--tenant-primary-color",
            primaryColor
        );
        document.body.classList.add("tenant-theme");
    } else {
        document.documentElement.style.removeProperty("--tenant-primary-color");
        document.body.classList.remove("tenant-theme");
    }
};

/**
 * 테넌트 URL 생성
 * 개발: lgcns.localhost:3300
 * 프로덕션: lgcns.aiqlab.tv
 * 스테이징: lgcns.staging.aiqlab.tv
 */
export const getTenantUrl = (subdomain: string): string => {
    if (isLocalDevelopment()) {
        const hostname = window.location.hostname;
        if (hostname.endsWith(".nip.io")) {
            const base = getBaseDomain(hostname);
            const port = window.location.port || "3300";
            return `http://${subdomain}.${base}:${port}`;
        }
        return `http://${subdomain}.localhost:3300`;
    }
    const baseDomain = getBaseDomain(window.location.hostname) || "aiqlab.tv";
    return `https://${subdomain}.${baseDomain}`;
};

/**
 * 랜딩 페이지 URL 생성
 * 개발: localhost:3300
 * 프로덕션: aiqlab.tv
 * 스테이징: staging.aiqlab.tv
 */
export const getLandingUrl = (): string => {
    if (isLocalDevelopment()) {
        const hostname = window.location.hostname;
        if (hostname.endsWith(".nip.io")) {
            const base = getBaseDomain(hostname);
            const port = window.location.port || "3300";
            return `http://${base}:${port}`;
        }
        return `http://localhost:3300`;
    }
    const baseDomain = getBaseDomain(window.location.hostname) || "aiqlab.tv";
    return `https://${baseDomain}`;
};
